import { ApolloClient, CombinedGraphQLErrors, InMemoryCache, split } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { ApolloProvider as BaseApolloProvider } from '@apollo/client/react';
import { useHostAuth } from '../shared/ui';
import { createClient } from 'graphql-ws';
import { useMemo, type ReactNode } from 'react';
import { environment } from '../../environments/environment';

/**
 * The same link chain the Angular portal had: batched HTTP for queries and
 * mutations, a WebSocket for subscriptions, a Bearer token from Auth0, and an
 * error link that logs rather than swallows.
 */
const errorLink = onError(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) => {
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  } else {
    console.error(`[Network error]: ${error}`);
  }
});

export function ApolloProvider({ children }: { children: ReactNode }) {
  const { getAccessToken, isAuthenticated } = useHostAuth();

  const client = useMemo(() => {
    const authLink = setContext(async (_, { headers }) => {
      if (!isAuthenticated) {
        return { headers: { ...headers, authorization: '' } };
      }
      const token = await getAccessToken();
      return { headers: { ...headers, authorization: `Bearer ${token}` } };
    });

    const httpLink = new BatchHttpLink({ uri: environment.graphql.uri });

    const wsLink = new GraphQLWsLink(
      createClient({
        url: environment.graphql.wsUri,
        connectionParams: async () => ({ authentication: await getAccessToken() }),
      }),
    );

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      errorLink.concat(authLink.concat(httpLink)),
    );

    return new ApolloClient({
      link: splitLink,
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: { notifyOnNetworkStatusChange: false },
      },
    });
  }, [getAccessToken, isAuthenticated]);

  return <BaseApolloProvider client={client}>{children}</BaseApolloProvider>;
}
